/**
 * Legend inventory for the "Total value delivered" stacked bar (#249).
 *
 * The legend must be an inventory of the bar, not a schema of the product: a
 * chip is a claim that its color exists somewhere in the bar, and a reader
 * hunting for a promised-but-missing segment stops trusting the number. So:
 *
 *  - a category the practice has never had (no history at all) gets NO chip —
 *    a medical-only practice never sees "VSP write-offs prevented";
 *  - a category the practice does have, at $0 for the selected period, gets a
 *    muted chip labeled "$0 this period" — visible, honest, and clearly not a
 *    missing segment;
 *  - everything with value this period gets a normal chip, in bar order.
 */

export interface RoiLegendSeg {
  key: string
  name: string
  cls: string
  /** Category value for the selected range — what the bar renders. */
  periodUsd: number
  /** Category value across the practice's full fetched history — applicability. */
  everUsd: number
}

export interface RoiLegendEntry {
  key: string
  name: string
  cls: string
  /** Applicable category with no value in the selected period → muted "$0" chip. */
  zeroThisPeriod: boolean
}

/** A period value under half a dollar renders as "$0" everywhere (usd formats
 *  with maximumFractionDigits: 0) and its bar segment is invisibly thin — so
 *  the chip mutes at the same threshold the display rounds at, instead of a
 *  full-strength chip promising a segment nobody can find. */
const ZERO_DISPLAY_THRESHOLD_USD = 0.5

export function buildRoiLegend(segs: RoiLegendSeg[]): RoiLegendEntry[] {
  return segs
    // periodUsd > 0 is belt-and-braces: period months are a subset of the
    // fetched history, so ever ≥ period — but a chip must exist for any
    // rendered segment even if the history math ever drifts.
    .filter(s => s.everUsd > 0 || s.periodUsd > 0)
    .map(s => ({
      key: s.key, name: s.name, cls: s.cls,
      zeroThisPeriod: s.periodUsd < ZERO_DISPLAY_THRESHOLD_USD,
    }))
}
